import React from 'react';
import { X, Phone, Clock, DollarSign, MessageSquare, FileText } from 'lucide-react';
import { AgentCall } from '../types/agentCall';
import { formatDateTime } from '../utils/textUtils';

interface AgentCallDetailModalProps {
  call: AgentCall | null;
  open: boolean;
  onClose: () => void;
}

const getSourceLabel = (source: string | null): string => {
  if (!source) return 'Desconocido';

  const labels: Record<string, string> = {
    'elevenlabs_web': 'Web (ElevenLabs)',
    'elevenlabs_whatsapp': 'WhatsApp (ElevenLabs)',
    'elevenlabs_phone': 'Teléfono (ElevenLabs)'
  };

  return labels[source] || source;
};

const AgentCallDetailModal: React.FC<AgentCallDetailModalProps> = ({ call, open, onClose }) => {
  if (!open || !call) return null;

  const dateStr = call.started_at || call.created_at;
  const date = new Date(dateStr);
  const minutes = call.duration_seconds
    ? Math.round(call.duration_seconds / 60)
    : null;

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
      <div className="bg-white rounded-lg shadow-xl max-w-4xl w-full max-h-[90vh] overflow-hidden flex flex-col">
        {/* Header */}
        <div className="flex items-center justify-between p-6 border-b border-gray-200">
          <div className="flex items-center">
            <Phone className="h-6 w-6 text-blue-600 mr-3" />
            <h2 className="text-2xl font-bold text-gray-900">
              Llamada del asistente - {formatDateTime(date)}
            </h2>
          </div>
          <button
            onClick={onClose}
            className="text-gray-400 hover:text-gray-600 transition-colors"
          >
            <X className="h-6 w-6" />
          </button>
        </div>

        {/* Content */}
        <div className="flex-1 overflow-y-auto p-6 space-y-6">
          {/* Información General */}
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            {/* Agente */}
            <div className="bg-gray-50 rounded-lg p-4">
              <div className="flex items-center mb-2">
                <Phone className="h-5 w-5 text-gray-600 mr-2" />
                <h3 className="text-sm font-semibold text-gray-700">Agente</h3>
              </div>
              <p className="text-gray-900 font-medium">
                {call.agent_name || 'Sin nombre'}
              </p>
            </div>

            {/* Canal */}
            <div className="bg-gray-50 rounded-lg p-4">
              <div className="flex items-center mb-2">
                <MessageSquare className="h-5 w-5 text-gray-600 mr-2" />
                <h3 className="text-sm font-semibold text-gray-700">Canal</h3>
              </div>
              <p className="text-gray-900 font-medium">
                {call.channel || 'N/A'}
              </p>
            </div>

            {/* Origen */}
            <div className="bg-gray-50 rounded-lg p-4">
              <div className="flex items-center mb-2">
                <FileText className="h-5 w-5 text-gray-600 mr-2" />
                <h3 className="text-sm font-semibold text-gray-700">Origen</h3>
              </div>
              <p className="text-gray-900 font-medium">
                {getSourceLabel(call.source)}
              </p>
            </div>

            {/* Duración */}
            <div className="bg-gray-50 rounded-lg p-4">
              <div className="flex items-center mb-2">
                <Clock className="h-5 w-5 text-gray-600 mr-2" />
                <h3 className="text-sm font-semibold text-gray-700">Duración</h3>
              </div>
              <p className="text-gray-900 font-medium">
                {minutes !== null ? `${minutes} min` : 'N/A'}
              </p>
            </div>
          </div>

          {/* Coste LLM */}
          {call.llm_cost !== null && (
            <div className="bg-yellow-50 rounded-lg p-4 border border-yellow-200">
              <div className="flex items-center mb-2">
                <DollarSign className="h-5 w-5 text-yellow-700 mr-2" />
                <h3 className="text-sm font-semibold text-yellow-900">Coste LLM</h3>
              </div>
              <p className="text-yellow-900 font-medium">
                {call.llm_cost.toFixed(6)} {call.currency || 'USD'}
              </p>
            </div>
          )}

          {/* Resumen */}
          {call.summary && (
            <div className="bg-blue-50 rounded-lg p-4 border border-blue-200">
              <h3 className="text-sm font-semibold text-blue-900 mb-3">Resumen</h3>
              <p className="text-blue-900 whitespace-pre-wrap">{call.summary}</p>
            </div>
          )}

          {/* Transcripción Completa */}
          {call.transcript && (
            <div className="bg-gray-50 rounded-lg p-4 border border-gray-200">
              <h3 className="text-sm font-semibold text-gray-900 mb-3">
                Transcripción completa
              </h3>
              <div className="bg-white rounded-lg p-4 max-h-96 overflow-y-auto border border-gray-200">
                <pre className="text-sm text-gray-800 whitespace-pre-wrap font-sans">
                  {call.transcript}
                </pre>
              </div>
            </div>
          )}

          {/* Metadata */}
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4 pt-4 border-t border-gray-200">
            <div>
              <p className="text-xs text-gray-500">ID de Conversación</p>
              <p className="text-sm text-gray-900 font-mono">
                {call.elevenlabs_conversation_id}
              </p>
            </div>
            <div>
              <p className="text-xs text-gray-500">Fecha de Registro</p>
              <p className="text-sm text-gray-900">
                {formatDateTime(new Date(call.created_at))}
              </p>
            </div>
          </div>
        </div>

        {/* Footer */}
        <div className="flex justify-end gap-3 p-6 border-t border-gray-200 bg-gray-50">
          <button
            onClick={onClose}
            className="px-4 py-2 text-gray-700 bg-white border border-gray-300 rounded-md hover:bg-gray-50 transition-colors"
          >
            Cerrar
          </button>
        </div>
      </div>
    </div>
  );
};

export default AgentCallDetailModal;
